/**
 * Shared markdown body styles for SafeMarkdown, preview editors, and read views.
 * Apply via `'& .markdown-body': markdownBodySx` or spread on a `.markdown-body` container.
 */
import { colors } from '../../designTokens';
/** Standard capability/prompt body typography — keep edit and preview in sync. */
export const capabilityTextSx = {
  fontSize: '0.95rem',
  lineHeight: 1.6,
  fontFamily: 'inherit',
  color: 'text.secondary',
};

/** Instructions / knowledge body — primary slate text in edit and preview. */
export const instructionBodyTextSx = {
  ...capabilityTextSx,
  color: colors.textPrimary,
};

/** Capability detail modal — description overview (readable prose, not metadata). */
export const promptDetailDescriptionBodySx = {
  fontSize: '0.875rem',
  lineHeight: 1.55,
  fontFamily: 'inherit',
  color: '#475569',
};

/** Shared inset padding for MarkdownPreviewEditor surfaces (builder, modal, knowledge). */
export const editorSurfacePaddingSx = {
  px: 2,
  py: 1.5,
};

/** Bordered description field — project, capability, and knowledge builders. */
export const borderedDescriptionShellSx = {
  position: 'relative',
  width: '100%',
  border: '1px solid',
  borderColor: 'rgba(15, 23, 42, 0.08)',
  borderRadius: '8px',
  p: { xs: 1.5, sm: 2 },
  bgcolor: 'background.paper',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&:focus-within': {
    borderColor: 'rgba(29, 78, 216, 0.45)',
    boxShadow: '0 0 0 3px rgba(29, 78, 216, 0.08)',
  },
};

/** Shared outlined control treatment for project, capability, and knowledge builders. */
export const builderOutlinedControlSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'background.paper',
    borderRadius: '8px',
    '& fieldset': { borderColor: 'divider' },
  },
};

/** Multiline TextField inside borderedDescriptionShellSx — no nested input chrome. */
export const embeddedMarkdownTextFieldSx = {
  width: '100%',
  '& .MuiInputBase-root': {
    px: 0,
    py: 0,
    alignItems: 'flex-start',
    bgcolor: 'transparent',
    '&:before, &:after': { display: 'none' },
    '& fieldset': { border: 'none' },
  },
  '& .MuiInputBase-input': {
    ...instructionBodyTextSx,
    fontSize: '0.875rem',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    padding: 0,
  },
};

/** Context creator content area — same font in edit (textarea) and preview (markdown). */
export const contextContentTypographySx = {
  ...capabilityTextSx,
  color: 'text.primary',
};

/** {{variable}} tokens — blue text only (no chip background). */
export const variableTokenChipSx = {
  color: 'primary.main',
  fontWeight: 500,
};

export const variableTokenChipInteractiveSx = {
  ...variableTokenChipSx,
  cursor: 'pointer',
  userSelect: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
};

export const markdownBodySx = {
  ...capabilityTextSx,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  '& > :first-of-type': { mt: 0 },
  '& > :last-child': { mb: 0 },
  '& p': { mb: 1, whiteSpace: 'normal' },
  '& ul, & ol': { pl: 3, mb: 1, whiteSpace: 'normal' },
  '& li': { whiteSpace: 'normal' },
  '& pre': {
    p: 1.5,
    borderRadius: 1,
    bgcolor: 'action.hover',
    overflow: 'auto',
    mb: 1.5,
    whiteSpace: 'pre-wrap',
  },
  '& pre:last-child': { mb: 0 },
  '& pre code': {
    bgcolor: 'transparent',
    px: 0,
    py: 0,
  },
  '& code': {
    px: 0.4,
    py: 0.15,
    borderRadius: 0.5,
    bgcolor: 'action.hover',
    fontSize: '0.9em',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    mt: 2,
    mb: 1,
    fontWeight: 600,
  },
  '& h1:first-child, & h2:first-child, & h3:first-child': { mt: 0 },
  '& blockquote': {
    borderLeft: '4px solid',
    borderColor: 'divider',
    pl: 2,
    ml: 0,
    fontStyle: 'italic',
    color: 'text.secondary',
    mb: 1.5,
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    mb: 1.5,
    display: 'block',
    overflowX: 'auto',
  },
  '& th, & td': {
    border: '1px solid',
    borderColor: 'divider',
    px: 1.5,
    py: 0.75,
    textAlign: 'left',
    verticalAlign: 'top',
  },
  '& th': {
    fontWeight: 600,
    bgcolor: 'action.hover',
  },
  '& tr:nth-of-type(even) td': {
    bgcolor: 'rgba(0, 0, 0, 0.02)',
  },
};

/** Knowledge read modal — markdown body (primary text, not grey). */
export const contextContentMarkdownBodySx = {
  ...markdownBodySx,
  color: colors.textPrimary,
};

export const promptDetailDescriptionPreviewSx = {
  px: 0,
  py: 0,
  '& .markdown-body': {
    ...markdownBodySx,
    ...promptDetailDescriptionBodySx,
    '& p': { mb: 0.75, lineHeight: 1.55, whiteSpace: 'normal' },
    '& p:last-child': { mb: 0 },
    '& h2': {
      mt: 1.5,
      mb: 0.5,
      fontSize: '0.875rem',
      lineHeight: 1.45,
      fontWeight: 700,
    },
    '& h2:first-child': { mt: 0 },
  },
};

/** Knowledge/instruction preview — match edit spacing: single newline = line break, blank line = paragraph gap. */
export const instructionPreviewMarkdownSx = {
  ...markdownBodySx,
  color: 'text.primary',
  '& p': { mb: 0, mt: 0, lineHeight: 1.6, whiteSpace: 'normal' },
  '& p + p': { mt: '1.6em' },
  '& ul, & ol': { mb: 0, pl: 3 },
  '& li + li': { mt: 0 },
};

/** Keep capability section headings at the compact Description scale in every preview. */
export const capabilityInstructionPreviewMarkdownSx = {
  ...instructionPreviewMarkdownSx,
  '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 1.5, mb: 0.5, fontSize: '0.875rem', lineHeight: 1.45, fontWeight: 700 },
  '& h1:first-child, & h2:first-child, & h3:first-child, & h4:first-child, & h5:first-child, & h6:first-child': { mt: 0 },
};

/** Capability README preview — operational sections should not read like page titles. */
export const capabilityDescriptionPreviewMarkdownSx = {
  ...capabilityInstructionPreviewMarkdownSx,
  '& h2, & h3, & h4': {
    mt: 1.5,
    mb: 0.5,
    fontSize: '0.875rem',
    lineHeight: 1.45,
    fontWeight: 700,
  },
  '& h2:first-child, & h3:first-child, & h4:first-child': { mt: 0 },
};

/** Compact variant for chat bubbles and smaller surfaces. */
export const markdownBodyCompactSx = {
  ...markdownBodySx,
  '& p': { mb: 0.75 },
  '& table': { mb: 1 },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    mt: 1,
    mb: 0.5,
    fontWeight: 600,
  },
};

/** Execute/builder assistant chat — primary slate body text (not text.secondary). */
export const assistantChatMarkdownSx = {
  ...markdownBodyCompactSx,
  color: colors.textPrimary,
  '& p, & li, & ul, & ol, & td, & th, & strong, & em, & h1, & h2, & h3, & h4, & h5, & h6': {
    color: colors.textPrimary,
  },
  '& blockquote': {
    borderLeft: '4px solid',
    borderColor: 'divider',
    pl: 2,
    ml: 0,
    fontStyle: 'italic',
    color: colors.textPrimary,
    mb: 1.5,
  },
};
